/**
 * VResizeableBoxSk is a selection box applied on top of the google chart
 * when a user shift-clicks onto it. The user can drag the selection up
 * and/or down and this module will estimate the difference between the
 * values based on the start and end cursor positions.
 */ "use strict";
Object.defineProperty(exports, "__esModule", {
    value: true
});
Object.defineProperty(exports, "VResizableBoxSk", {
    enumerable: true,
    get: function() {
        return VResizableBoxSk;
    }
});
const _lit = require("lit");
const _decorators = require("lit/decorators.js");
function _ts_decorate(decorators, target, key, desc) {
    var c = arguments.length, r = c < 3 ? target : desc === null ? desc = Object.getOwnPropertyDescriptor(target, key) : desc, d;
    if (typeof Reflect === "object" && typeof Reflect.decorate === "function") r = Reflect.decorate(decorators, target, key, desc);
    else for(var i = decorators.length - 1; i >= 0; i--)if (d = decorators[i]) r = (c < 3 ? d(r) : c > 3 ? d(target, key, r) : d(target, key)) || r;
    return c > 3 && r && Object.defineProperty(target, key, r), r;
}
function _ts_metadata(k, v) {
    if (typeof Reflect === "object" && typeof Reflect.metadata === "function") return Reflect.metadata(k, v);
}
var _VResizableBoxSk;
const fontPosition = {
    upper: -35,
    lower: -10
};
let VResizableBoxSk = (_VResizableBoxSk = class VResizableBoxSk extends _lit.LitElement {
    render() {
        if (!this.delta) {
            return (0, _lit.html)``;
        }
        return (0, _lit.html)`
      <p class=${this.lowerFont ? 'lower' : 'upper'}>
        ${this.delta.raw} or ${this.delta.percent}%
      </p>
      <md-elevation></md-elevation>
    `;
    }
    /**
   * Initialize helper variables and show the vertical selection.
   * By default, show the selection as the same width as the google
   * chart area.
   * @param chartArea key positions of the google chart. Top is used to
   * determine if the delta font should be above or below the selection.
   * Left and width allow the selection to cleanly cover the chart.
   * @param start the coordinate and y value of the chart where the cursor is
   */ show(chartArea, start) {
        this.start = start;
        this.updateDelta(start.value);
        this.topBoundary = chartArea.top;
        this.lowerFont = start.coord + fontPosition.upper < this.topBoundary;
        this.style.top = `${start.coord}px`;
        this.style.left = `${chartArea.left}px`;
        this.style.width = `${chartArea.width}px`;
        this.style.display = 'block';
    }
    /**
   * Hide the module by switching the display to none
   */ hide() {
        this.style.display = 'none';
        this.style.height = `0px`;
    }
    /**
   * Update the height and deltas as the user moves the selection up and/or down
   * @param update the updated cursor coordinate and respective y-axis chart value
   */ updateSelection(update) {
        this.updateDelta(update.value);
        this.updateBox(update.coord);
        // lower the font if the selection's upper boundary is too close to the top
        // boundary of the graph
        const selectionTopCoord = Math.min(this.start.coord, update.coord);
        this.lowerFont = selectionTopCoord + fontPosition.upper < this.topBoundary;
    }
    // update the height of the box relative to the cursor start point
    updateBox(newY) {
        const newTop = Math.min(newY, this.start.coord);
        const height = Math.abs(newY - this.start.coord);
        this.style.top = `${newTop}px`;
        this.style.height = `${height}px`;
    }
    // update the deltas relative to the selection starting value
    updateDelta(b) {
        const a = this.start.value;
        this.delta = {
            // only display 4 significant digits for readability
            raw: (b - a).toPrecision(4),
            // show NaN if start value is exactly 0
            percent: a === 0 ? 'NaN' : ((b - a) / a * 100).toPrecision(4)
        };
    }
    getDelta() {
        return this.delta;
    }
    constructor(...args){
        super(...args);
        // the starting cursor location and value of the box relative
        // to the parent plot-google-chart
        // start represents either the top or bottom of the selection range
        // we need to track the start coordinate because getBoundingClientRect
        // is with respect to the viewport
        this.start = {
            coord: 0,
            value: 0
        };
        // stores the selection range delta in units of the traces as a string
        // rounded to 4 significant digits (for readability)
        this.delta = null;
        // if the selection is too tall, then the delta font will break out
        // of the google chart and conflict with other visual elements
        // when that happens, lower the font
        this.lowerFont = false;
        // the top boundary of the chart. Used to calculate if the selection
        // range is too close to the top boundary.
        this.topBoundary = null;
    }
}, (()=>{
    // TODO(b/361365957): create a light/dark mode theme color + opacity
    // and apply variables to the border-color and background-color
    _VResizableBoxSk.styles = (0, _lit.css)`
    :host {
      display: none;
      position: absolute;
      border-style: solid;
      border-width: 1px 0; /* no borders left and right */
      border-color: rgb(255 255 0 / 50%); /* yellow */
      background-color: rgb(255 255 0 / 20%); /* almost transparent yellow */
      height: 0;

      --md-elevation-level: 1;

      cursor: row-resize;
      pointer-events: none; /* allows pointer events to occur underneath */
    }

    p {
      position: absolute;
      left: 30px;
      font-size: 16px;
      text-shadow:
        -1px -1px 0 var(--md-sys-color-background, 'white'),
        1px -1px 0 var(--md-sys-color-background, 'white'),
        -1px 1px 0 var(--md-sys-color-background, 'white'),
        1px 1px 0 var(--md-sys-color-background, 'white');
    }

    /* show the delta calculation slightly above the box */
    p.upper {
      top: ${fontPosition.upper}px;
    }

    /* show the delta calculation slightly below the box */
    p.lower {
      top: ${fontPosition.lower}px;
    }
  `;
})(), _VResizableBoxSk);
_ts_decorate([
    (0, _decorators.property)()
], VResizableBoxSk.prototype, "start", void 0);
_ts_decorate([
    (0, _decorators.property)(),
    _ts_metadata("design:type", Object)
], VResizableBoxSk.prototype, "delta", void 0);
_ts_decorate([
    (0, _decorators.property)()
], VResizableBoxSk.prototype, "lowerFont", void 0);
VResizableBoxSk = _ts_decorate([
    (0, _decorators.customElement)('v-resizable-box-sk')
], VResizableBoxSk);
